import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { ShiftWindow } from "../../utils/scheduleConflict";

// The section's guess when the header carries no time range: a 7:00 start, as the dialog always used.
const DEFAULT_START = { hour: 7, minute: 0 };
const DEFAULT_SHIFT_HOURS = 12;

type Params = { date: DateTime; weekIndex: number; dayIndex: number; selectedTimeRange?: { start: DateTime; end: DateTime } };

/**
 * The "+" dialog's times: the section's own (the header's time range, else a 7:00 / 12h guess on that day), the
 * user's edits, and the window the dialog last set itself. The current window differing from that seeded one is
 * how the dialog tells the user's own times from ones it filled in. `seedVersion` changes on every seed so the
 * time picker — which keeps its own copy of the times — remounts to show them.
 */
export const useQuickAddTimes = ({ date, weekIndex, dayIndex, selectedTimeRange }: Params) => {
	const { hospitalData, timezone } = useHospitalData();

	const sectionWindow = useMemo<ShiftWindow>(() => {
		if (selectedTimeRange) return { startTime: selectedTimeRange.start.setZone(timezone), endTime: selectedTimeRange.end.setZone(timezone) };
		// Fall back to first shift's start time
		const dayDate = hospitalData.weeks[weekIndex]?.weeklyShifts[0]?.days[dayIndex]?.date;
		const startTime = (dayDate ?? date).setZone(timezone).set(DEFAULT_START);
		return { startTime, endTime: startTime.plus({ hours: DEFAULT_SHIFT_HOURS }) };
	}, [selectedTimeRange, hospitalData.weeks, weekIndex, dayIndex, date, timezone]);

	const [window, setWindow] = useState<ShiftWindow>(sectionWindow);
	const [seed, setSeed] = useState<{ version: number; window: ShiftWindow }>({ version: 0, window: sectionWindow });

	/** Set the times on the dialog's own behalf (not an edit by the user). */
	const seedWindow = useCallback((next: ShiftWindow) => {
		setWindow(next);
		setSeed((previous) => ({ version: previous.version + 1, window: next }));
	}, []);

	// The time picker reports its selection on every render, not only on a change, so an unchanged report must keep
	// the same window object — a fresh one would re-render the dialog, and the picker would report again.
	const editWindow = useCallback(
		(startTime: DateTime, endTime: DateTime) =>
			setWindow((previous) => (previous.startTime === startTime && previous.endTime === endTime ? previous : { startTime, endTime })),
		[],
	);

	return { window, sectionWindow, seededWindow: seed.window, seedVersion: seed.version, seedWindow, editWindow };
};
